import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SavedFilters } from '@air/api';
import { type CreateSavedFilterInput, type SavedFilter, type SavedFiltersListResponse } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getPrivateSavedSearchKey, isPrivateSavedSearchKey } from '~/components/v2/hooks/queries/usePrivateSavedSearch';
import { isPrivateSavedSearchListKey } from '~/components/v2/hooks/queries/usePrivateSavedSearchList';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { createEmptyListResponseStructure } from '~/utils/ApiUtils';
import { getSavedFilterKey } from './useGetSavedFilter';
import { getSavedFiltersKey } from './useGetSavedFilters';

export const useCreateSavedFilter = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const createSavedFilter = useMutation({
    mutationFn: (savedFilter: CreateSavedFilterInput) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      return SavedFilters.createSavedFilter({ workspaceId, savedFilter });
    },
    // When mutate is called:
    onMutate: async () => {
      if (!currentWorkspace?.id) {
        return;
      }

      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: getSavedFiltersKey(currentWorkspace.id) });

      // Snapshot the previous value
      const previousSavedFilters = queryClient.getQueryData<SavedFiltersListResponse>(
        getSavedFiltersKey(currentWorkspace.id),
      );

      const previousPrivateSavedSearchList = queryClient.getQueriesData<SavedFiltersListResponse>({
        predicate: (query) => isPrivateSavedSearchListKey(query.queryKey),
      });

      /**
       * @TODO - At a later point, we should add the mutation logic here
       */

      // Return a context object with the snapshotted value
      return { previousSavedFilters, previousPrivateSavedSearchList };
    },
    onSuccess: (createdSavedFilter) => {
      if (!currentWorkspace?.id) {
        return;
      }

      /** Inject the new saved filter into local cache */
      queryClient.setQueryData<SavedFilter>(getSavedFilterKey(createdSavedFilter.id), createdSavedFilter);
      queryClient.setQueryData<SavedFilter>(
        getPrivateSavedSearchKey({ id: createdSavedFilter.id }),
        createdSavedFilter,
      );

      /** Inject the new saved filter into local cache for the saved filters list */
      queryClient.setQueryData<SavedFiltersListResponse>(getSavedFiltersKey(currentWorkspace.id), (cache) =>
        cache
          ? {
              ...cache,
              data: [...cache.data, createdSavedFilter],
            }
          : {
              ...createEmptyListResponseStructure(),
              data: [createdSavedFilter],
              total: 1,
            },
      );

      queryClient.invalidateQueries({ predicate: (query) => isPrivateSavedSearchKey(query.queryKey) });
      queryClient.invalidateQueries({ predicate: (query) => isPrivateSavedSearchListKey(query.queryKey) });
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (error, savedFilter, context) => {
      if (!currentWorkspace?.id) {
        return;
      }

      reportErrorToBugsnag({
        error,
        context: 'Unable to create saved filter',
        metadata: {
          key: 'Data',
          data: { savedFilter, workspaceId: currentWorkspace?.id },
        },
      });

      queryClient.setQueryData(getSavedFiltersKey(currentWorkspace.id), context?.previousSavedFilters);

      context?.previousPrivateSavedSearchList.forEach((query) => {
        queryClient.setQueryData<SavedFiltersListResponse>(...query);
      });
    },
  });

  return {
    createSavedFilter,
  };
};
